/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserNotificationAction } from '@arc/arc-shared-frontend';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActionCreator, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, map, take } from 'rxjs/operators';
import {
  AccountPaymentMethod,
  BtPayContext,
  GetAllPaymentAccountsResponseV1,
  LoadingSpinnerService,
  PaymentAccount,
  PaymentAccountsHelperService,
  PaymentAccountsServiceFactory,
  PaymentAccountStatus,
  PaymentRulesResponseV1,
  RedirectStateOneTimePaymentAccount,
} from '@arc/unified-payments-frontend/shared-payment';
import { PaymentRuleStore } from '../../payment-rules';
import { BtPayPaymentAccountsActions } from '../actions';
import { BtPayPaymentAccountsActionsUnion } from '../actions/btpay-payment-accounts.actions';
import {
  PaymentStoreState,
  selectRedirectOtpPaymentAccounts,
} from '../reducers/btpay.reducers';
import {
  selectPaymentAccountsRequestData,
  selectVirtualOtherwisePrimaryTenantId,
} from '../../session-store';
import {
  HostedPaymentsPadResultComponentPaymentRulesActions,
  PoaPadResultComponentPaymentRulesActions,
  PoiPadResultComponentPaymentRulesActions,
} from '../../component-actions';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class BtPayPaymentAccountsEffects {
  constructor(
    private actions$: Actions<BtPayPaymentAccountsActionsUnion>,
    private store$: Store<PaymentStoreState>,
    private paymentAccountServiceFactory: PaymentAccountsServiceFactory,
    private paymentAccountHelperService: PaymentAccountsHelperService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {}

  loadAvailablePaymentAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BtPayPaymentAccountsActions.LoadAvailablePaymentAccounts.type),
      concatLatestFrom(() => [
        this.store$.pipe(select(PaymentRuleStore)),
        this.store$.select(selectVirtualOtherwisePrimaryTenantId),
      ]),
      exhaustMap(([action, paymentRules, tenantId]) =>
        this.loadPaymentAccounts(
          tenantId,
          action.buyerAccountIdentifier,
          action.btPayContext,
          paymentRules,
          action.externalUserUrn,
        ),
      ),
    ),
  );

  loadAvailablePaymentAccountsAfterRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PoaPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
        HostedPaymentsPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
        PoiPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
      ),
      concatLatestFrom(() => [
        this.store$.pipe(select(PaymentRuleStore)),
        this.store$.select(selectVirtualOtherwisePrimaryTenantId),
        this.store$.select(selectPaymentAccountsRequestData),
        this.store$.select(selectRedirectOtpPaymentAccounts),
      ]),
      exhaustMap(
        ([, paymentRules, tenantId, requestData, redirectOtpPaymentAccounts]) =>
          this.loadPaymentAccounts(
            tenantId,
            requestData.buyerAccountIdentifier,
            requestData.btPayContext,
            paymentRules,
            requestData.externalUserUrn,
            redirectOtpPaymentAccounts,
            BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsAfterRedirectSuccess,
            BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsAfterRedirectFailure,
          ),
      ),
    ),
  );

  /* istanbul ignore next */
  loadUntilGetPaymentAccountsFinished$ = createEffect(
    () =>
      this.loadUntilActionsComplete(
        [BtPayPaymentAccountsActions.LoadAvailablePaymentAccounts.type],
        [
          BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsSuccess.type,
          BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsFailure.type,
        ],
      ),
    { dispatch: false },
  );

  private loadPaymentAccounts(
    tenantId: string,
    buyerAccountIdentifier: string,
    btPayContext: BtPayContext,
    paymentRules: PaymentRulesResponseV1,
    externalUserUrn: string,
    otpPaymentAccounts?: RedirectStateOneTimePaymentAccount[],
    success?: ActionCreator<
      string,
      (props: { paymentAccounts: PaymentAccount[] }) => any
    >,
    errorAction?: ActionCreator<
      string,
      (props: { error: HttpErrorResponse }) => any
    >,
  ) {
    const getAllPaymentAccountRequest$: Observable<GetAllPaymentAccountsResponseV1> =
      this.getAllPaymentAccountRequest(
        tenantId,
        buyerAccountIdentifier,
        btPayContext,
        externalUserUrn,
      );

    return getAllPaymentAccountRequest$.pipe(
      map((getAllPaymentsResponse: GetAllPaymentAccountsResponseV1) => {
        const { acceptBank } = paymentRules.settings.payments;
        const { acceptCreditCard } = paymentRules.settings.payments;
        const allowableCardBrands =
          paymentRules.settings.payments.allowableCardBrands?.map((x) =>
            x.substring(0, 1).toUpperCase(),
          ) || [];
        const isAccountSourceEuroDirectDebit =
          btPayContext.paymentAccountsSources.includes('EuroDirectDebit');

        const nonStripePaymentAccounts =
          getAllPaymentsResponse.paymentAccounts.filter(
            (x) => x.gatewayTokenIssuer !== 'Stripe',
          );

        const filteredPaymentAccounts = isAccountSourceEuroDirectDebit
          ? nonStripePaymentAccounts
          : nonStripePaymentAccounts.filter(
              (x) =>
                (x.paymentMethod === AccountPaymentMethod.Ach && acceptBank) ||
                (x.paymentMethod === AccountPaymentMethod.Card &&
                  acceptCreditCard &&
                  this.isAcceptedBrand(allowableCardBrands, x.cardNetwork)),
            );

        filteredPaymentAccounts.forEach((value) => {
          value.dbDisplayName = value.displayName;

          if (value.paymentMethod === AccountPaymentMethod.Card) {
            value.cardType =
              this.paymentAccountHelperService.getAureusCardTypeFromCardNetwork(
                value.cardNetwork,
              );
          }
          value.isDisabled =
            value.accountStatus == PaymentAccountStatus.Pending ||
            value.accountStatus == PaymentAccountStatus.Suspended ||
            value.accountStatus == PaymentAccountStatus.Cancelled;
        });

        const mergedPaymentAccounts = this.mergePaymentAccounts(
          otpPaymentAccounts,
          filteredPaymentAccounts,
        );

        return success
          ? success({ paymentAccounts: mergedPaymentAccounts })
          : BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsSuccess({
              paymentAccounts: mergedPaymentAccounts,
            });
      }),
      catchError((error: HttpErrorResponse) =>
        of(
          errorAction
            ? errorAction({ error })
            : BtPayPaymentAccountsActions.LoadAvailablePaymentAccountsFailure({
                error,
              }),
          new UserNotificationAction({
            type: 'error',
            message:
              'An error occurred retrieving your payment method(s) data. Try again later.',
          }),
        ),
      ),
    );
  }

  private isAcceptedBrand(allowableCardBrands: string[], cardBrand: string) {
    return (
      allowableCardBrands.length === 0 ||
      allowableCardBrands.indexOf(cardBrand.toUpperCase()) !== -1
    );
  }

  private mergePaymentAccounts(
    otpPaymentAccounts: RedirectStateOneTimePaymentAccount[],
    paymentAccounts: PaymentAccount[],
  ) {
    if (!otpPaymentAccounts) {
      return paymentAccounts;
    }

    const mergedAccounts = [...paymentAccounts];

    otpPaymentAccounts.forEach((otpAccount) => {
      const mappedAccount: PaymentAccount = {
        paymentAccountIdentifierBurn: otpAccount.paymentAccountIdentifierBurn,
        displayName: otpAccount.displayName,
        paymentMethod: otpAccount.paymentMethod,
        nameOnCard: otpAccount.nameOnCard,
        bankName: otpAccount.bankName,
        nameOnBankAccount: otpAccount.nameOnBankAccount,
        accountLast4: otpAccount.accountLast4,
        expirationYear: otpAccount.expirationYear,
        expirationMonth: otpAccount.expirationMonth,
        cardNetwork: otpAccount.cardNetwork,
        createdWithCardOverride: otpAccount.createdWithCardOverride,
        shouldRequestCvv: otpAccount.shouldRequestCvv,
        dbDisplayName: otpAccount.displayName,
        isOneTimePaymentAccount: true,
        isDisabled: false,
        showBadge: false,
      };

      if (mappedAccount.paymentMethod === AccountPaymentMethod.Card) {
        mappedAccount.cardType =
          this.paymentAccountHelperService.getAureusCardTypeFromCardNetwork(
            mappedAccount.cardNetwork,
          );
      }

      mergedAccounts.push(mappedAccount);
    });

    return mergedAccounts;
  }

  private getAllPaymentAccountRequest(
    tenantId: string,
    buyerAccountIdentifier: string,
    btPayContext: BtPayContext,
    externalUserUrn: string,
  ) {
    if (externalUserUrn !== null) {
      return this.paymentAccountServiceFactory
        .create()
        .getAllPaymentAccountsV1ByUsers(
          tenantId,
          buyerAccountIdentifier,
          btPayContext,
          externalUserUrn,
        );
    }
    return this.paymentAccountServiceFactory
      .create()
      .getAllPaymentAccountsV1(tenantId, buyerAccountIdentifier, btPayContext);
  }

  /* istanbul ignore next */
  private loadUntilActionsComplete(
    sourceActionTypes: string[],
    completeActionTypes: string[],
  ) {
    return this.actions$.pipe(
      ofType(...sourceActionTypes),
      exhaustMap(() => {
        const completeActions$ = this.actions$.pipe(
          ofType(...completeActionTypes),
          take(1),
        );

        this.loadingSpinnerService.loadUntil(completeActions$);

        return of();
      }),
    );
  }
}
